import PatientProfileI, { DemographicsI, IPatientProfile, IdentificationI, PatientRelationshipI, PersonalInformation } from "@interfaces/patient/patient-profileI";
import { PatientBackgroundI } from "@interfaces/patient/patient-backgroundI";
import { generateUrl } from "@services/generateUrl/generateUrl";
import axiosApiInstance from "./custom-axios-instance";
import { RecordItemI, RecordPractitionerI, RecordSummaryI, RecordSummaryQueryParamsI, RecordsPaginationI, RecordsQueryParamsI } from "@interfaces/scheduller/record-itemI";
import { readBrowserTimeZone } from "@functions/time-zone";
import { PatientBasicInformationI, PatientItemDrProx } from "@interfaces/patients/patients";
import PatientIndicatorsI, { RiskIncatorsColorsI } from "@interfaces/patient/patient-indicators";
import { AddressI, ContactPointI } from "@interfaces/miscellaneous/miscellaneous";
import { difficultyI } from "@interfaces/patients/recordI";
import signupServiceApiInstance from "./clients/signup.old";
import emrServiceApiInstance from "./clients/emr";
import fcUserServiceApiInstance from "./clients/fc-user";

/**
 * @deprecated
 */
export async function readPatientProfile(patientUsername: string): Promise<PatientProfileI> 
{
    const endpoint = `/person/patient/profile/${patientUsername}`;
    const data = await signupServiceApiInstance.get<PatientProfileI>(endpoint);
    return data.data;
}

export function readPatientProfileByDrProx(
    patientEmail: string,
    responsable: boolean,
    usernamePractitioner: string
): Promise<PatientRelationshipI> {
    return axiosApiInstance
        .get(
            `${generateUrl()}/drprox/patient/email?email=${patientEmail}&isResponsable=${responsable}&usernamePractitioner=${usernamePractitioner}`
        )
        .then((data) => {
            const organization = data.data as unknown;
            return organization as PatientRelationshipI;
        });
}
export function readPatientProfileByDrProxAndOrganizationId(
    patientEmail: string,
    responsable: boolean,
    organizationId: number,
    usernamePractitioner: string
): Promise<PatientRelationshipI> {
    return axiosApiInstance
        .get(
            `${generateUrl()}/drprox/patient/email?email=${patientEmail}&isResponsable=${responsable}&organizationId=${organizationId}&usernamePractitioner=${usernamePractitioner}`
        )
        .then((data) => {
            const organization = data.data as unknown;
            return organization as PatientRelationshipI;
        });
}

export function updatePatientProfile(
    patientInformation: PersonalInformation
): Promise<{ code: string, message: string }> {
    return axiosApiInstance
        .put(
            `${generateUrl()}/signup/person/patient/profile/personal/information`,
            patientInformation
        )
        .then((data) => {
            const organization = data.data as unknown;
            return organization as { code: string, message: string };
        });
}
/**
 * @deprecated
 */
export function updatePatientProfileFullData(
    patientInformation: PatientProfileI
): Promise<{ code: string, message: string }> {
    return axiosApiInstance
        .put(
            `${generateUrl()}/signup/person/patient/profile/update/`,
            patientInformation
        )
        .then((response) => {
            const data = response.data as unknown;
            return data as { code: string; message: string };
        });
}

export async function updatePatientProfileData(data: Omit<IPatientProfile, "contactPoints">): Promise<number>
{
    const endpoint = "/api/v1/person/patient/profile/update";
    const response = await fcUserServiceApiInstance.put(endpoint, data);
    return response.status;
}

export function getPatientAddresses(username: string) {
    return axiosApiInstance
        .get(
            `${generateUrl()}/signup/person/patient/profile/address/${username}`
        )
        .then((response) => {
            const data = response.data as unknown;
            return data as AddressI[];
        });
}
export function updatePatientAddress(addresses: AddressI[], username: string) {
    return axiosApiInstance
        .put(
            `${generateUrl()}/signup/person/patient/profile/address/${username}`,
            addresses
        )
        .then((response) => {
            const data = response.data as unknown;
            return data as { code: string; message: string };
        });
}

export function getPatientIdentifications(username: string) {
    return axiosApiInstance
        .get(
            `${generateUrl()}/signup/person/patient/profile/identifications/${username}`
        )
        .then((response) => {
            const data = response.data as unknown;
            return data as IdentificationI[];
        });
}

export function updatePatientIdentification(
    identifications: IdentificationI[],
    username: string
) {
    return axiosApiInstance
        .put(
            `${generateUrl()}/signup/person/patient/profile/identifications/${username}`,
            identifications
        )
        .then((response) => {
            const data = response.data as unknown;
            return data as { code: string; message: string };
        });
}



export function updatePatientContactMeans(
    contactPoints: ContactPointI[],
    username: string
) {
    return axiosApiInstance
        .put(
            `${generateUrl()}/signup/person/patient/profile/contact/means/${username}`,
            contactPoints
        )
        .then((response) => {
            const data = response.data as unknown;
            return data as { code: string; message: string };
        });
}
export function updatePatientDemographics(demographics: DemographicsI) {
    return axiosApiInstance
        .put(
            `${generateUrl()}/signup/person/patient/profile/demographic/data`,
            demographics
        )
        .then((response) => {
            const data = response.data as unknown;
            return data as { code: string; message: string };
        });
}

export function readPatientHubioId(
    patientUsername: string
): Promise<string> {
    return axiosApiInstance
        .get(
            `${generateUrl()}/signup/user/hubio-id/${patientUsername}`
        )
        .then((data) => {
            const organization = data.data as unknown;
            return organization as string;
        });
}

export function getPatientsList(practitionerUsername: string) {
    return axiosApiInstance
        .get(
            `${generateUrl()}/scheduler/practitioner/${practitionerUsername}/patient/`
        )
        .then((data) => {
            const organization = data.data as unknown;
            return organization as PatientBasicInformationI[];
        });
}

export function getPatientsListDrProx(practitionerUsername: string) {
    return axiosApiInstance
        .get(
            `${generateUrl()}/drprox/practitioner/patients/${practitionerUsername}`
        )
        .then((data) => {
            const organization = data.data as unknown;
            return organization as PatientItemDrProx[];
        });
}
export function getPatientsListDrProxTimeZoneAndCurrentDate(
    practitionerUsername: string,
    currentDate:string,
    lang:string,
    timezone: string
    ) {
    return axiosApiInstance
        .get(
            `${generateUrl()}/drprox/practitioner/patients/${practitionerUsername}?currentDate=${currentDate}&lang=${lang}&timezone=${timezone}`
        )
        .then((data) => {
            const organization = data.data as unknown;
            return organization as PatientItemDrProx[];
        });
}
/**
 * @deprecated use `getPatientsListDrProxByOrganization` from `apps\dr-prox-front-react\src\services\patients\patients.ts`
 */
export function getPatientsListDrProxByOrganization(organizationId: number, 
    currentDate:string,
    lang:string,
    timezone: string,) {
    return axiosApiInstance
        .get(
            `${generateUrl()}/drprox/practitioner/patients/organization/${organizationId}?currentDate=${currentDate}&lang=${lang}&timezone=${timezone}`
        )
        .then((data) => {
            const organization = data.data as unknown;
            return organization as PatientItemDrProx[];
        });
}
/**
 * @deprecated
 */
export function getPatientsListDrProxByOrganizationTimeZoneAndCurrentDate(
    organizationId: number,
    practitionerUsername: string,
    currentDate:string,
    lang:string,
    timezone: string,
    ) {
    return axiosApiInstance
        .get(
            `${generateUrl()}/drprox/practitioner/patients/organization/${organizationId}/${practitionerUsername}?currentDate=${currentDate}&lang=${lang}&timezone=${timezone}`
        )
        .then((data) => {
            const organization = data.data as unknown;
            return organization as PatientItemDrProx[];
        });
}
/**
 * @deprecated
 */
export function getPatientsListDrProxByOrganizationAndProfessionalTimeZoneAndCurrentDate(
    organizationId: number,
    currentDate:string,
    lang:string,
    timezone: string
    ) {
    return axiosApiInstance
        .get(
            `${generateUrl()}/drprox/practitioner/patients/organization/${organizationId}?currentDate=${currentDate}&lang=${lang}&timezone=${timezone}`
        )
        .then((data) => {
            const organization = data.data as unknown;
            return organization as PatientItemDrProx[];
        });
}

export function readPatientBackground(
    patientId: number,
    language: string
): Promise<PatientBackgroundI> {
    return axiosApiInstance
        .get(
            `${generateUrl()}dynamicsection/question/${patientId}/${language}/4
            `
        )
        .then((data) => {
            const organization = data.data as unknown;
            return organization as PatientBackgroundI;
        });
}


export function readPatientRecords(recordsQuery: RecordsQueryParamsI): Promise<RecordsPaginationI> {
    const readPath =
        `${generateUrl()}/emr/appointment-record/patient/${recordsQuery.patientUsername}?` +
        `${
            recordsQuery.lastDocumentId
                ? `lastDocumentId=${recordsQuery.lastDocumentId}&`
                : ""
        }` +
        `pageSize=${recordsQuery.pageSize}` +
        `&timezone=${readBrowserTimeZone()}` +
        `${recordsQuery.healthServiceId ? recordsQuery.healthServiceId : ""}` + 
        `${recordsQuery.historyDate ? recordsQuery.historyDate : ""}` + 
        `${recordsQuery.reasonCode ? recordsQuery.reasonCode : ""}` + 
        `${recordsQuery.temporalRelationshipCode ? recordsQuery.temporalRelationshipCode : ""}` +
        `${recordsQuery.usernamePractitioner ? recordsQuery.usernamePractitioner : ""}`+
        `${recordsQuery.isRelatedPregnancy ? recordsQuery.isRelatedPregnancy : ""}`+
        `${recordsQuery.firstInYear ? recordsQuery.firstInYear : ""}`;
    return axiosApiInstance.get(readPath).then((data) => {
        const records = data.data as unknown;
        return records as RecordsPaginationI;
    });
}
export function readPatientRecordsIndexBeetweenDates(
    patientUsername: string,
    initDate: string,
    endDate: string,
    timeZone: string,
    pageSize?: number
): Promise<RecordsPaginationI> {
    const readPath =
        `${generateUrl()}/emr/appointment-record/patient/${patientUsername}/dates?endDate=${endDate}&initDate=${initDate}&pageSize=${pageSize}&timezone=${timeZone}`;  
    return axiosApiInstance.get(readPath).then((data) => {
        const records = data.data as unknown;
        return records as RecordsPaginationI;
    });
}

export function readPatientRecordsBetweenDates(
    patientUsername: string,
    initDate: string,
    endDate: string,
    timeZone: string
): Promise<RecordItemI[]> {
    const readPath =
        `${generateUrl()}/emr/record/${patientUsername}/dates?endDate=${endDate}&initDate=${initDate}&timeZone=${timeZone}`;
    return axiosApiInstance.get(readPath).then((data) => {
        const records = data.data as unknown;
        return records as RecordItemI[];
    });
}

export function getPatientRecordSummary(data: RecordSummaryQueryParamsI){
    return axiosApiInstance
        .get(
            `${generateUrl()}/emr/appointment-record/summary?referenceId=${data.referenceId}&timezone=${data.timezone}&type=${data.type}`
        )
        .then((res) => {
            const data = res.data;
            return data as RecordSummaryI;
        });
}

export function getPtientRecordPractitioners(username: string){
    return axiosApiInstance
        .get(
            `${generateUrl()}/emr/appointment-record/patient/${username}/allpractitioner`
        )
        .then((res => {
            const data = res.data;
            return data as RecordPractitionerI[];
        }));
}

export function readPatientIndicators(username: string): Promise<PatientIndicatorsI> {
    const readPath =
        `${generateUrl()}/index/hubio-indicators/${username}`;
    return axiosApiInstance.get(readPath).then((data) => {
        const records = data.data as unknown;
        return records as PatientIndicatorsI;
    });
}

export function readRiskIndicatorsColors(): Promise<RiskIncatorsColorsI[]> {
    const readPath =
        `${generateUrl()}/dynamicsection/risk/indicator/colors`;
    return axiosApiInstance.get(readPath).then((data) => {
        const records = data.data as unknown;
        return records as RiskIncatorsColorsI[];
    });
}

export function readUserAvatar(
    patientUsername: string,
    rol: string
): Promise<Blob> {
    return axiosApiInstance
        .get(
            `${generateUrl()}/storage/storage/profile/image?username=${patientUsername}&rol=${rol}
        `,
            {
                responseType: "blob",
            }
        )
        .then((data) => {
            const identifier = data.data as unknown;
            return identifier as Blob;
        });
}

export function getPatientRecordStatus(
    patientUsername: string,
    practitionerUsername: string
): Promise<boolean> {
    return axiosApiInstance
        .get(
            `${generateUrl()}/emr/main/record/edit/${patientUsername}/${practitionerUsername}`
        )
        .then((response) => {
            const data = response.data.response as unknown;
            return data as boolean;
        });
}
/**
 * Get difficulties list by patient username.
 * @returns List of difficulties
 */
export async function getDifficultiesByPatientUserName(patientUsername: string) 
{
    const endpoint = `/difficulties/user/${patientUsername}`;
    const response = await emrServiceApiInstance.get<difficultyI[]>(endpoint);
    return response.data;
}
/**
 * Remove difficulties by ids array.
 * @returns Status response
 */
export async function removeDifficultiesByIds(difficultiesRemoved: string[], referenceId:string, typeReference?: "record" | "appointment", ) 
{
    let url = "/difficulties";
    const params = new URLSearchParams();
    if(referenceId )
        params.append("referenceId", referenceId);
    if(typeReference)
        params.append("typeReference", typeReference);

    const strParams = params.toString();
    if(strParams)
        url += `?${strParams}`;
    
    const response = await emrServiceApiInstance.delete<unknown>(url,{data:difficultiesRemoved});
    return response.status;
}
